import React, { useState, useEffect } from "react";
import { AlertCircle, Mail, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

interface PasswordResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// 定义重置密码流程的步骤
enum ResetStep {
  EMAIL_INPUT = 0,
  VERIFICATION_CODE = 1,
  NEW_PASSWORD = 2,
  SUCCESS = 3
}

const PasswordResetDialog = ({
  open,
  onOpenChange,
  email,
  onEmailChange,
}: PasswordResetDialogProps) => {
  const [currentStep, setCurrentStep] = useState<ResetStep>(ResetStep.EMAIL_INPUT);
  const [isLoading, setIsLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  
  const { toast: legacyToast } = useToast();
  const { resetPassword, sendOtp, verifyCode, checkEmailExists, resetPasswordWithCode } = useAuth();
  const navigate = useNavigate();

  // 处理倒计时逻辑
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  // 验证邮箱
  const validateEmail = () => {
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return false;
    }
    return true;
  };

  // 发送验证码
  const handleSendCode = async () => {
    if (countdown > 0) return;
    
    if (!validateEmail()) return;
    
    setSendingCode(true);
    setError("");
    
    try {
      // 检查邮箱是否存在
      const emailExists = await checkEmailExists(email);
      
      if (!emailExists) {
        setError("We couldn't find an account with that email address");
        return;
      }
      
      // 发送验证码
      await sendOtp(email, 'forget');
      
      setCodeSent(true);
      setCountdown(60);
      setCurrentStep(ResetStep.VERIFICATION_CODE);
      
      toast.success("Verification Code Sent", {
        description: "We've sent a verification code to your email. Please check your inbox.",
      });
    } catch (error: any) {
      setError(error.message || "Failed to send verification code. Please try again.");
      toast.error("Error Sending Code", {
        description: error.message || "Failed to send verification code. Please try again.",
      });
    } finally {
      setSendingCode(false);
    }
  };

  // 验证验证码
  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError("Please enter the verification code");
      return;
    }
    
    setIsLoading(true);
    setError("");
    
    try {
      const isValid = await verifyCode(email, verificationCode, 'forget');
      
      if (isValid) {
        setCurrentStep(ResetStep.NEW_PASSWORD);
      } else {
        setError("Invalid verification code. Please try again.");
      }
    } catch (error: any) {
      setError(error.message || "Failed to verify code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // 提交新密码
  const handleResetPassword = async () => {
    // 验证密码
    if (!newPassword) {
      setError("Please enter your new password");
      return;
    }
    
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    setIsLoading(true);
    setError("");
    
    try {
      // 调用API重置密码
      const success = await resetPasswordWithCode(email, verificationCode, newPassword);
      
      if (success) {
        // 保存成功重置的邮箱到本地存储
        localStorage.setItem('anzir_lastEmail', email);
        
        setCurrentStep(ResetStep.SUCCESS);
        toast.success("Password Reset Successful", {
          description: "Your password has been reset successfully. You can now login with your new password.",
        });
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } catch (error: any) {
      setError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // 处理成功后的登录跳转
  const handleBackToLogin = () => {
    // 关闭对话框
    handleDialogChange(false);
    
    // 导航到登录页面并传递邮箱参数
    navigate(`/login?email=${encodeURIComponent(email)}`);
  };

  // 根据当前步骤渲染相应内容
  const renderStepContent = () => {
    switch (currentStep) {
      case ResetStep.EMAIL_INPUT:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email Address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={onEmailChange}
                className="bg-black/50 border-white/20 text-white"
              />
            </div>
            
            <Button
              type="button"
              className="w-full bg-[#ff6c24] hover:bg-[#ff6c24]/90 text-white"
              onClick={handleSendCode}
              disabled={sendingCode}
            >
              {sendingCode ? (
                <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
              ) : "Send Verification Code"}
            </Button>
          </div>
        );
      
      case ResetStep.VERIFICATION_CODE:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="verification-code">Verification Code</Label>
              <div className="flex space-x-2 items-center mt-2">
                <div className="flex-grow relative">
                  <Input
                    id="verification-code"
                    type="text"
                    placeholder="Enter verification code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="bg-black/50 border-white/20 text-white"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleSendCode}
                  disabled={sendingCode || countdown > 0}
                  className={countdown > 0 
                    ? "h-10 min-w-[120px] bg-gray-600/50 text-gray-400 cursor-not-allowed rounded-md text-sm font-medium" 
                    : "h-10 min-w-[120px] bg-[#ff6c24] hover:bg-[#ff6c24]/90 text-white rounded-md text-sm font-medium"}
                >
                  {sendingCode ? (
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
                  ) : countdown > 0 ? (
                    <>
                      <span className="mr-1">Resend in</span>
                      <span className="font-bold">{countdown}s</span>
                    </>
                  ) : (
                    codeSent ? "Resend Code" : "Send Code"
                  )}
                </Button>
              </div>
            </div>
            
            <Button
              type="button"
              className="w-full bg-[#ff6c24] hover:bg-[#ff6c24]/90 text-white"
              onClick={handleVerifyCode}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
              ) : "Verify Code"}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              className="w-full bg-transparent border-white/20 text-white hover:bg-white/5"
              onClick={() => setCurrentStep(ResetStep.EMAIL_INPUT)}
              disabled={isLoading}
            >
              Back
            </Button>
          </div>
        );
      
      case ResetStep.NEW_PASSWORD:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-black/50 border-white/20 text-white pr-10"
                />
                <div
                  className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center cursor-pointer group"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500 group-hover:text-[#ff6c24] transition-colors" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500 group-hover:text-[#ff6c24] transition-colors" />
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-black/50 border-white/20 text-white pr-10"
                />
              </div>
            </div>
            
            <Button
              type="button"
              className="w-full bg-[#ff6c24] hover:bg-[#ff6c24]/90 text-white"
              onClick={handleResetPassword}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
              ) : "Reset Password"}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              className="w-full bg-transparent border-white/20 text-white hover:bg-white/5"
              onClick={() => setCurrentStep(ResetStep.VERIFICATION_CODE)}
              disabled={isLoading}
            >
              Back
            </Button>
          </div>
        );
      
      case ResetStep.SUCCESS:
        return (
          <div className="py-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#ff6c24]/20 flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-[#ff6c24]" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Password Reset Complete</h3>
            <p className="text-gray-400 mb-4">
              Your password has been reset successfully.<br />
              You can now log in with your new password.
            </p>
            <Button
              type="button"
              className="bg-[#ff6c24] hover:bg-[#ff6c24]/90 text-white w-full"
              onClick={handleBackToLogin}
            >
              Back to Login
            </Button>
          </div>
        );
        
      default:
        return null;
    }
  };

  const handleDialogChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when dialog closes
      setCurrentStep(ResetStep.EMAIL_INPUT);
      setVerificationCode("");
      setNewPassword("");
      setConfirmPassword("");
      setError("");
      setCodeSent(false);
      setCountdown(0);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="bg-black/80 border-white/10 text-white p-6 max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Reset Password</DialogTitle>
          <DialogDescription className="text-gray-400 mt-2">
            {currentStep === ResetStep.EMAIL_INPUT && "Enter your email address to reset your password."}
            {currentStep === ResetStep.VERIFICATION_CODE && "Please enter the verification code sent to your email."}
            {currentStep === ResetStep.NEW_PASSWORD && "Create a new password for your account."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {error && (
            <div className="bg-red-500/10 text-red-500 px-4 py-2 rounded flex items-center gap-2 mb-4">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          {renderStepContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordResetDialog;
